/**
 * ErrorBoundary — the client CRASH FLOOR. React has no built-in render-error containment, so WITHOUT
 * this a single render throw (a null deref on a drifted API shape, a `.map` on a non-array) unmounts
 * the ENTIRE React tree and leaves a blank white screen with no recovery but a manual reload.
 *
 * Three variants:
 *   - `root`  — wraps the whole app (both mount paths). Full-page fallback with a Reload. Last resort.
 *   - `route` — wraps the routed page inside AppShell, keyed on the pathname so navigating away CLEARS
 *               the error. Contained panel that keeps the shell chrome (header + nav) mounted and offers
 *               Try again (re-render the same route) + a link back to the dashboard.
 *   - `chrome` — wraps a data-driven shell-chrome strip rendered ABOVE the route boundary (e.g. the
 *               DataHealthBanner). A crash there must NEVER take down the whole app, so this variant
 *               degrades to a minimal, non-blocking inline notice (never the full-page reload) and the
 *               header + nav + page content around it stay mounted and usable.
 *
 * PHI-safety: mirrors reportMsalError — the caught Error object is NEVER passed to the logger (its
 * message could echo user input). Only the non-PHI error NAME + the component stack (component names)
 * are logged, so App Insights gets a debuggable breadcrumb without any patient data.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { logger } from "@skypointcloud/logger/react";

const log = logger.createComponentLogger("ErrorBoundary");

interface Props {
  children: ReactNode;
  variant?: "root" | "route" | "chrome";
  /** Change this (e.g. to the route pathname) to auto-clear a caught error on navigation. */
  resetKey?: string | number;
  /** Non-PHI label for telemetry (e.g. "route"). */
  boundaryName?: string;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidUpdate(prev: Props): void {
    // Navigating to a new route (resetKey changes) clears a previously-caught error so the new page
    // gets a fresh chance to render instead of staying stuck on the fallback.
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // NO PHI: never pass the Error (its message could echo input). Log the error NAME + component
    // stack (component names only) so the crash is debuggable in App Insights without patient data.
    log.error("React render error contained by boundary", undefined, {
      boundary: this.props.boundaryName ?? this.props.variant ?? "unknown",
      errorName: error?.name ?? "Error",
      componentStack: info?.componentStack ?? "",
    });
  }

  private reset = (): void => this.setState({ hasError: false });

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.variant === "root") return <RootFallback onReload={() => window.location.reload()} />;
    // chrome: a shell-strip crash is CONTAINED to a minimal inline notice — never the full-page reload,
    // and never an unmount of the surrounding shell (header + nav + page content stay live).
    if (this.props.variant === "chrome") return <ChromeFallback />;
    return <RouteFallback onRetry={this.reset} />;
  }
}

const outerStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  padding: 24,
  fontFamily: "system-ui",
  background: "#f6f7f9",
} as const;

const cardStyle = {
  maxWidth: 460,
  width: "100%",
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 32,
  textAlign: "center",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
} as const;

const primaryButton = {
  background: "#242424",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "10px 18px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
} as const;

/** Full-page fallback (whole-app crash). No react-router dependency — the root boundary is OUTSIDE the Router. */
function RootFallback({ onReload }: { onReload: () => void }) {
  return (
    <div style={outerStyle}>
      <div role="alert" style={cardStyle}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: "#101828", margin: "0 0 8px" }}>
          skyCare hit an unexpected problem
        </h1>
        <p style={{ fontSize: 14, color: "#475467", lineHeight: 1.5, margin: "0 0 20px" }}>
          Something went wrong while loading the app. Reloading usually fixes it.
        </p>
        <button type="button" onClick={onReload} style={primaryButton}>
          Reload skyCare
        </button>
      </div>
    </div>
  );
}

/**
 * Minimal, non-blocking fallback for a shell-chrome strip crash (e.g. the DataHealthBanner). It renders a
 * slim inline notice INSTEAD of unmounting the app — the header, nav, and page content around it are
 * untouched, so a chrome crash never becomes a full-page white-screen or "Reload skyCare". No PHI: the
 * copy is static and model-agnostic.
 */
function ChromeFallback() {
  return (
    <div
      role="status"
      style={{
        padding: "8px 32px",
        fontFamily: "system-ui",
        fontSize: 13,
        color: "#475467",
        background: "#f6f7f9",
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      This status strip is temporarily unavailable. The rest of the page is unaffected.
    </div>
  );
}

/** Contained fallback for a single page crash — the AppShell chrome stays mounted around it. */
function RouteFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <div role="alert" style={{ padding: 32, fontFamily: "system-ui" }}>
      <div style={{ ...cardStyle, margin: "24px auto 0" }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: "#101828", margin: "0 0 8px" }}>
          This page could not be displayed
        </h2>
        <p style={{ fontSize: 14, color: "#475467", lineHeight: 1.5, margin: "0 0 20px" }}>
          Something went wrong loading this screen. You can try again, or return to the dashboard.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button type="button" onClick={onRetry} style={primaryButton}>
            Try again
          </button>
          <a
            href="/"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "10px 18px",
              fontSize: 14,
              fontWeight: 600,
              color: "#242424",
              textDecoration: "none",
              border: "1px solid #d0d5dd",
              borderRadius: 8,
            }}
          >
            Go to dashboard
          </a>
        </div>
      </div>
    </div>
  );
}
